/**
 * Wie sich die Reiseliste ordnet — die laufende Reise obenauf, drei Gruppen.
 *
 * ## Warum es diese Datei gibt
 *
 * Aus demselben Grund wie `reiseabschluss.ts`, dessen Zweiteilung sie
 * ablöst: Die Entscheidung, welche Reise wohin rückt, ist die Art Regel, die
 * still kaputtgeht. Eine vertauschte Bedingung, und die abgeschlossene Reise
 * steht ganz oben als „läuft gerade" — im Markup wäre das nicht prüfbar, denn
 * die Tests dieses Projekts laufen ohne Browser.
 *
 * ## Vier Plätze, und warum es keine vier Gruppen sind
 *
 * - **Die laufende Reise** steht einzeln und ohne Überschrift ganz oben. Sie
 *   ist keine Gruppe: Es gibt sie höchstens einmal, und eine Überschrift über
 *   einer einzelnen Zeile behauptet eine Menge, die nicht existiert. Wer die
 *   Anwendung unterwegs öffnet, sucht genau diese eine Reise — sie soll ohne
 *   einen Klick dastehen und nicht hinter einer Klappe.
 * - **Geplante Reisen** (`planungsstand: 'fest'`) — durchgeplant, kann
 *   angetreten werden.
 * - **Reisen in Planung** (Vorgabe) — Ideen, halbe Routen, Entwürfe.
 * - **Abgeschlossen** — dieselbe Regel wie bisher (`istAbgeschlossen`).
 *
 * ## Der Planungsstand steht in `settings` und nicht in einer Spalte
 *
 * Aus den Daten ist „fest geplant" nicht abzuleiten, und zwar grundsätzlich
 * nicht: Eine Reise mit Datum, Stationen und Buchungen kann eine Idee sein,
 * und eine ohne jede Buchung kann feststehen. Es ist eine bewusste Marke des
 * Menschen — also genau das, wofür `settings` da ist. Eine eigene Spalte
 * hieße Migration, iOS-Modell und ein zweiter Schreibweg für ein Wort, das
 * nur diese eine Liste sortiert. Die Reisephase (unterwegs, abgeschlossen)
 * kommt weiterhin aus den Daten; sie ist ja messbar.
 *
 * **Reine Funktionen** — kein DOM, kein Netz, **keine Uhr**. `heute` kommt
 * herein, wie in `reisephase.ts` und aus demselben Grund: Eine Funktion, die
 * `new Date()` selbst ruft, ist um Mitternacht eine andere als eine Minute
 * davor. `localStorage` liest und schreibt die Komponente; hier gehen nur
 * Werte hinein und Werte heraus.
 */
import { istAbgeschlossen } from './reiseabschluss';
import { reisephase, type Phasenreise } from './reisephase';

/* ══ Der Planungsstand an der Reise ═══════════════════════════════════ */

/** Fest durchgeplant oder noch in Arbeit. Mehr Stufen sind nicht vorgesehen. */
export type Planungsstand = 'fest' | 'offen';

/**
 * Ohne Marke gilt „in Planung".
 *
 * Die harmlose Richtung: Eine Reise fälschlich unter „Geplante Reisen" sagt
 * „die kannst du antreten" über einen halben Entwurf. Andersherum steht eine
 * fertige Reise eine Gruppe zu tief, und ein Klick rückt sie hoch. Nebenbei
 * landet damit jede Reise aus der Zeit vor dieser Marke dort, wo sie hingehört.
 */
export const PLANUNGSSTAND_VORGABE: Planungsstand = 'offen';

/** Der Schlüssel in `trip.settings` — derselbe Name wie im Server. */
export const PLANUNGSSTAND = 'planungsstand';

/**
 * Was von einer Reise gebraucht wird — absichtlich nicht `Trip`.
 *
 * `Phasenreise` bringt `MitAbschluss` schon mit (Beginn, Ende, Abschluss);
 * dazu kommt hier nur der Einstellungsblock, in dem die Marke steht.
 */
export interface Gruppenreise extends Phasenreise {
  settings?: Record<string, unknown> | null;
}

/** Ist das ein Stand, den es gibt? */
export function istPlanungsstand(wert: unknown): wert is Planungsstand {
  return wert === 'fest' || wert === 'offen';
}

/**
 * Der Planungsstand dieser Reise.
 *
 * Alles, was nicht `'fest'` oder `'offen'` ist, ergibt die Vorgabe: kein
 * Schlüssel, `null`, ein Wert aus einer künftigen Fassung, Müll von Hand.
 * Dieselbe Nachsicht wie in `abschnittswuensche.wuenscheLesen` — ein Fehler
 * beim Lesen einer Anzeigevorliebe darf die Liste nicht kosten.
 */
export function planungsstandVon(reise: Gruppenreise | null | undefined): Planungsstand {
  const wert = reise?.settings?.[PLANUNGSSTAND];
  return istPlanungsstand(wert) ? wert : PLANUNGSSTAND_VORGABE;
}

/**
 * Den Stand setzen — und den **ganzen** Einstellungsblock zurückgeben.
 *
 * `settings` wird beim Patch als Ganzes ersetzt (siehe `db.update_trip`).
 * Wer hier nur `{ planungsstand }` schickte, verlöre Straßenvorgaben,
 * Vignetten und die Titelbildwahl, und zwar lautlos. Wortgleich zu
 * `titelbild.mitTitelbild` — dieselbe Falle, dieselbe Antwort.
 *
 * `'offen'` **löscht** den Schlüssel, statt ihn hinzuschreiben: Der fehlende
 * Schlüssel ist die Vorgabe, und zwei Schreibweisen für denselben Zustand
 * wären eine Gelegenheit, sie auseinanderlaufen zu lassen. Der Server tut
 * dasselbe (`_planungsstand_pruefen`); dass beide Seiten es tun, ist Absicht
 * — die Oberfläche soll nicht darauf angewiesen sein, dass er aufräumt.
 */
export function mitPlanungsstand(
  settings: Record<string, unknown> | null | undefined,
  stand: Planungsstand
): Record<string, unknown> {
  const raus = { ...(settings ?? {}) };
  if (stand === 'fest') raus[PLANUNGSSTAND] = stand;
  else delete raus[PLANUNGSSTAND];
  return raus;
}

/**
 * Darf an dieser Reise umgeschaltet werden?
 *
 * An einer abgeschlossenen nicht — dort ist die Frage beantwortet, und ein
 * Schalter „fest geplant" unter einer gefahrenen Reise wäre eine Handlung
 * ohne Wirkung. An der laufenden auch nicht: Sie steht ohnehin oben, und die
 * beiden Gruppen darunter sind Zukunft. Ein Knopf, der nichts sichtbar tut,
 * ist schlimmer als keiner.
 */
export function kannPlanungsstandWaehlen(
  reise: Gruppenreise | null | undefined,
  heute: string
): boolean {
  if (!reise || istAbgeschlossen(reise)) return false;
  return reisephase(reise, heute) !== 'unterwegs';
}

/* ══ Der Klapp-Zustand der Gruppen ════════════════════════════════════ */

/** Die drei Gruppen unter der laufenden Reise. */
export type Gruppenid = 'geplant' | 'planung' | 'abgeschlossen';

/** Ihre Überschriften — einmal, damit Liste und Test dieselben Wörter nennen. */
export const GRUPPENTITEL: Record<Gruppenid, string> = {
  geplant: 'Geplante Reisen',
  planung: 'Reisen in Planung',
  abgeschlossen: 'Abgeschlossen'
};

/**
 * Die Reihenfolge der Gruppen. Sie ist eine Aussage und keine Sortierung:
 * Was als Nächstes gefahren wird, steht oben; was gefahren ist, unten.
 */
export const GRUPPEN_ORDNUNG: readonly Gruppenid[] = ['geplant', 'planung', 'abgeschlossen'] as const;

/**
 * Der Speicherplatz — im Namensraum `reise.` wie alles andere im Gerätespeicher
 * dieser App (`reise.station-abschnitte`, `reise.briefing-zu`).
 *
 * Ohne Konto- und ohne Reise-Kennung: Wer die abgeschlossenen zuklappt, will
 * sie zu haben, und zwar überall. Derselbe Griff wie beim globalen
 * Abschnittswunsch, mit demselben Preis — ein Klick nimmt es zurück.
 */
export const GRUPPEN_SCHLUESSEL = 'reise.reisegruppen';

/**
 * Gemerkt wird, **was zugeklappt ist** — nicht der ganze Zustand.
 *
 * Anders als beim Stationsblatt (`abschnittswuensche.ts`) reicht das hier,
 * und der Unterschied ist der Grund: Dort wechselt die Vorgabe mit jeder
 * Station, hier ist sie fest und heißt „offen". Ein Speicher aus Zuständen
 * wäre dort nach drei Klicks eine Liste von Zufällen; hier ist die Liste der
 * zugeklappten Gruppen die kürzeste wahre Fassung.
 */
export function istZugeklappt(id: Gruppenid, zu: readonly Gruppenid[]): boolean {
  return zu.includes(id);
}

/** Ist das eine Gruppe, die es gibt? */
export function istGruppenid(wert: unknown): wert is Gruppenid {
  return typeof wert === 'string' && (GRUPPEN_ORDNUNG as readonly string[]).includes(wert);
}

/**
 * Die zugeklappten Gruppen aus dem Speichertext lesen — tolerant.
 *
 * Alles, was nicht passt, ergibt „nichts zugeklappt": kein Eintrag, kaputtes
 * JSON, ein Aufbau, den es nie gab, eine Kennung aus einer künftigen Fassung.
 * Dieselbe Überlegung wie in `abschnittswuensche.wuenscheLesen` — ein
 * `JSON.parse`-Wurf beim Aufbau der Komponente kostete die ganze Reiseliste,
 * und die harmlose Richtung ist die, in der etwas auftaucht statt verschwindet.
 */
export function zuLesen(roh: string | null | undefined): Gruppenid[] {
  if (!roh) return [];
  try {
    const daten: unknown = JSON.parse(roh);
    if (!Array.isArray(daten)) return [];
    const raus: Gruppenid[] = [];
    for (const x of daten) {
      if (istGruppenid(x) && !raus.includes(x)) raus.push(x);
    }
    return raus;
  } catch {
    return [];
  }
}

/** Die zugeklappten Gruppen als Speichertext. */
export function zuSchreiben(zu: readonly Gruppenid[]): string {
  return JSON.stringify(zu);
}

/**
 * Einen Griff festhalten — als **neue** Liste.
 *
 * Unveränderlich, damit Svelte den Wechsel bemerkt; dieselbe Überlegung wie
 * bei `einklappen.umschalten` und `abschnittswuensche.wunschSetzen`.
 */
export function klappUmschalten(zu: readonly Gruppenid[], id: Gruppenid): Gruppenid[] {
  return istZugeklappt(id, zu) ? zu.filter((x) => x !== id) : [...zu, id];
}

/* ══ Die Aufteilung selbst ════════════════════════════════════════════ */

/** Eine Gruppe mit Inhalt — leere gibt es nicht, siehe `reisegruppen`. */
export interface Reisegruppe<T> {
  id: Gruppenid;
  titel: string;
  reisen: T[];
}

/** Was die Liste zeichnet: oben die laufende(n), darunter die Gruppen. */
export interface Reiseordnung<T> {
  laufend: T[];
  gruppen: Reisegruppe<T>[];
}

/**
 * Die Reiseliste in ihre Plätze teilen.
 *
 * **Die Reihenfolge innerhalb der Gruppen bleibt, wie sie hereinkam.** Der
 * Server sortiert nach `updated_at`, und weder Phase noch Planungsstand ändern
 * daran etwas — sie entscheiden nur, in welchem Block eine Reise steht. Wer
 * hier zusätzlich sortierte, ordnete die Liste bei jedem Umlegen neu, und der
 * Nutzer suchte die Reise, die er gerade angefasst hat. (Dieselbe Zusage wie
 * in `reiseabschluss.gruppiereReisen`, deren Aufgabe diese Funktion übernimmt.)
 *
 * **`laufend` ist eine Liste und kein einzelner Wert**, obwohl es fast immer
 * genau eine Reise ist. Zwei sich überlappende Reisen sind kein verbotener
 * Zustand — der Server verhindert sie nicht —, und eine Funktion, die dann
 * eine davon unterschlägt, ließe eine Reise spurlos aus der Übersicht
 * verschwinden. Die Oberfläche zeichnet sie untereinander, ohne Überschrift.
 *
 * **Leere Gruppen kommen gar nicht erst zurück.** Eine Überschrift über
 * nichts ist der Leerzustand, den das Designsystem ausdrücklich nicht will:
 * Sie behauptet eine Gruppe, die es nicht gibt. Das hier zu entscheiden statt
 * im Markup ist der ganze Zweck dieser Datei — dort wäre es nicht prüfbar.
 */
export function reisegruppen<T extends Gruppenreise>(
  reisen: readonly T[],
  heute: string
): Reiseordnung<T> {
  const laufend: T[] = [];
  const nachId: Record<Gruppenid, T[]> = { geplant: [], planung: [], abgeschlossen: [] };

  for (const t of reisen) {
    // Abgeschlossen schlägt alles — `reisephase` sagt an einer abgehakten
    // Reise ohnehin `nachlese`, auch wenn ihr Kalender noch läuft.
    if (istAbgeschlossen(t)) {
      nachId.abgeschlossen.push(t);
      continue;
    }
    if (reisephase(t, heute) === 'unterwegs') {
      laufend.push(t);
      continue;
    }
    nachId[planungsstandVon(t) === 'fest' ? 'geplant' : 'planung'].push(t);
  }

  return {
    laufend,
    gruppen: GRUPPEN_ORDNUNG.filter((id) => nachId[id].length).map((id) => ({
      id,
      titel: GRUPPENTITEL[id],
      reisen: nachId[id]
    }))
  };
}
